import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Building2,
  ExternalLink,
  Layers,
  Package,
  ChevronLeft,
  ChevronRight,
  X,
  Mail,
  Send,
  Info,
  Star,
  MessageCircle,
  PlayCircle,
} from "lucide-react";
import { normalizeSector } from "../utils/normalizeSector";

/* ─── helpers ─── */
const toTitleTR = (s = "") =>
  s
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toLocaleUpperCase("tr") + w.slice(1) : ""))
    .join(" ");

const formatPhone = (raw = "") => {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0"))
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(
      7,
      9
    )} ${digits.slice(9)}`;
  if (digits.length === 10)
    return `(0${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(
      6,
      8
    )} ${digits.slice(8)}`;
  return raw;
};

const getYoutubeEmbedUrl = (url = "") => {
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (!match) return null;
  return `https://www.youtube.com/embed/${match[1]}`;
};

/* ─── Logo ─── */
const logoCache = new Map();
const FirmaLogo = React.memo(({ logo, name, size = "lg" }) => {
  const initials = (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toLocaleUpperCase("tr") || "")
    .join("");
  const [status, setStatus] = useState(() => {
    if (!logo) return "none";
    if (logoCache.has(logo)) return logoCache.get(logo);
    return "loading";
  });
  const sz =
    size === "lg"
      ? "w-28 h-28 rounded-2xl text-xl"
      : "w-16 h-16 rounded-xl text-base";

  if (!logo || status === "error" || status === "none")
    return (
      <div
        className={`${sz} bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-200 flex items-center justify-center shrink-0`}
      >
        <span className="font-semibold text-slate-400 tracking-wide">
          {initials}
        </span>
      </div>
    );

  return (
    <div
      className={`${sz} bg-white border border-slate-200 overflow-hidden shrink-0 relative`}
    >
      {status !== "ok" && (
        <div className={`${sz} bg-slate-100 animate-pulse absolute inset-0`} />
      )}
      <img
        src={logo}
        alt={name}
        onLoad={() => {
          logoCache.set(logo, "ok");
          setStatus("ok");
        }}
        onError={() => {
          logoCache.set(logo, "error");
          setStatus("error");
        }}
        loading="eager"
        decoding="async"
        className={`w-full h-full object-contain transition-opacity duration-300 ${
          status === "ok" ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
});

/* ─── Harita ─── */
const FirmaMap = ({ firma }) => {
  const addressQuery = useMemo(() => {
    const parts = [firma.adres, firma.ilce, firma.il].filter(Boolean);
    return encodeURIComponent(toTitleTR(parts.join(", ")));
  }, [firma]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapSrc = `https://maps.google.com/maps?q=${addressQuery}&output=embed&hl=tr&z=15`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${addressQuery}`;

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100 relative">
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
            Harita
          </span>
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition px-2 py-1 rounded-md hover:bg-slate-50"
        >
          <ExternalLink className="w-3 h-3" /> Google Maps'te aç
        </a>
      </div>
      {!mapLoaded && (
        <div className="absolute inset-0 top-[41px] bg-slate-100 animate-pulse z-10 flex flex-col items-center justify-center gap-2">
          <MapPin className="w-6 h-6 text-slate-300 animate-bounce" />
          <span className="text-xs text-slate-400">Harita yükleniyor…</span>
        </div>
      )}
      <iframe
        src={mapSrc}
        title="harita"
        width="100%"
        height="280"
        style={{ border: 0, display: "block" }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        onLoad={() => setMapLoaded(true)}
        className="relative z-0"
      />
    </div>
  );
};

/* ─── Ürün Lightbox ─── */
const Lightbox = ({ product, onClose, onPrev, onNext, hasPrev, hasNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl overflow-hidden shadow-2xl max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/90 shadow hover:bg-white transition"
        >
          <X className="w-4 h-4 text-slate-700" />
        </button>

        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.ad}
            className="w-full max-h-[60vh] object-contain bg-slate-50"
          />
        ) : (
          <div className="w-full h-48 bg-slate-100 flex items-center justify-center">
            <Package className="w-12 h-12 text-slate-300" />
          </div>
        )}

        <div className="p-5">
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            {product.ad}
          </h3>
          {product.aciklama && (
            <p className="text-sm text-slate-500 leading-relaxed">
              {product.aciklama}
            </p>
          )}
        </div>

        {(hasPrev || hasNext) && (
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex items-center justify-between px-3 pointer-events-none">
            <button
              onClick={onPrev}
              disabled={!hasPrev}
              className="pointer-events-auto p-2 rounded-full bg-white/90 shadow hover:bg-white transition disabled:opacity-20"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <button
              onClick={onNext}
              disabled={!hasNext}
              className="pointer-events-auto p-2 rounded-full bg-white/90 shadow hover:bg-white transition disabled:opacity-20"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Ürünler Bölümü ─── */
const ProductsSection = ({ products = [] }) => {
  const [activeIdx, setActiveIdx] = useState(null);
  if (!products.length) return null;

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
            <Package className="w-4 h-4 text-amber-500" />
          </div>
          <h2 className="text-sm font-semibold text-slate-800">
            Ürünler & Hizmetler
          </h2>
          <span className="ml-auto text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            {products.length} ürün
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {products.map((p, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              className="group text-left rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 hover:shadow-md transition-all duration-200"
            >
              <div className="aspect-square bg-slate-50 overflow-hidden relative">
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.ad}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-8 h-8 text-slate-200 group-hover:text-slate-300 transition" />
                  </div>
                )}
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors duration-200" />
              </div>
              <div className="p-2.5">
                <p className="text-xs font-semibold text-slate-800 line-clamp-1">
                  {p.ad}
                </p>
                {p.aciklama && (
                  <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2 leading-relaxed">
                    {p.aciklama}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {activeIdx !== null && (
        <Lightbox
          product={products[activeIdx]}
          onClose={() => setActiveIdx(null)}
          onPrev={() => setActiveIdx((i) => Math.max(0, i - 1))}
          onNext={() =>
            setActiveIdx((i) => Math.min(products.length - 1, i + 1))
          }
          hasPrev={activeIdx > 0}
          hasNext={activeIdx < products.length - 1}
        />
      )}
    </>
  );
};

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="h-12 bg-[#243f4d] flex items-center px-4 relative">
    <Icon className="w-5 h-5 text-amber-400 mr-3" />
    <h2 className="text-white font-extrabold uppercase text-sm">{title}</h2>

    <span className="absolute -bottom-2 left-8 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-[#243f4d]" />
  </div>
);

const RatingBox = ({ rating, setRating }) => {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden mt-4">
      <SectionTitle icon={Star} title="Firmayı Puanla" />

      <div className="p-4">
        <p className="text-sm text-slate-500 font-medium mb-3">
          Bu firmaya puan verin.
        </p>

        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              className="transition hover:scale-110"
            >
              <Star
                className={`w-7 h-7 ${
                  star <= rating
                    ? "fill-amber-400 text-amber-400"
                    : "text-slate-300"
                }`}
              />
            </button>
          ))}
        </div>

        {rating > 0 && (
          <p className="mt-3 text-xs text-slate-500">
            Seçilen puan: <b>{rating}/5</b>
          </p>
        )}
      </div>
    </div>
  );
};

const CommentSection = ({
  rating,
  setRating,
  commentText,
  setCommentText,
  commentLoading,
  onSubmit,
  comments = [],
  commentSuccess,
}) => {
  const sortedComments = [...comments].sort((a, b) => {
    const dateA = a.tarih?.seconds || 0;
    const dateB = b.tarih?.seconds || 0;
    return dateB - dateA;
  });

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <SectionTitle icon={MessageCircle} title="Firma Hakkında Yorum Yap" />

        <div className="p-4 space-y-4">
          <div>
            <p className="text-sm text-slate-600 font-semibold mb-2">
              Puanınız
            </p>

            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="transition hover:scale-110"
                >
                  <Star
                    className={`w-6 h-6 ${
                      star <= rating
                        ? "fill-amber-400 text-amber-400"
                        : "text-slate-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Firma hakkındaki yorumunuzu yazın..."
            rows={4}
            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-amber-400 resize-none"
          />

          <button
            type="button"
            onClick={onSubmit}
            disabled={commentLoading}
            className="h-11 px-5 rounded-lg bg-[#243f4d] hover:bg-[#1d3441] text-white text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {commentLoading ? "Gönderiliyor..." : "Yorumu Gönder"}
          </button>
          {commentSuccess && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 font-medium">
              Yorumunuz başarıyla gönderildi. Yayınlanmadan önce moderatör
              onayından geçecektir.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <SectionTitle
          icon={MessageCircle}
          title="Firma Hakkında Yapılan Yorumlar"
        />

        <div className="p-4 space-y-3">
          {sortedComments.length === 0 ? (
            <p className="text-sm text-slate-400">
              Bu firma için henüz yorum yapılmamış.
            </p>
          ) : (
            sortedComments.map((comment, index) => (
              <div
                key={index}
                className="border border-slate-100 rounded-lg p-4 bg-slate-50"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-sm font-bold text-slate-800">
                      {comment.name}
                    </p>

                    <div className="flex items-center gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= Number(comment.puan)
                              ? "fill-amber-400 text-amber-400"
                              : "text-slate-300"
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  <span className="text-xs text-slate-400">
                    {comment.tarih?.toDate
                      ? comment.tarih.toDate().toLocaleDateString("tr-TR")
                      : ""}
                  </span>
                </div>

                <p className="text-sm text-slate-600 leading-6">
                  {comment.yorum}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Ana Sayfa ─── */
const FirmDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [firm, setFirm] = useState(null);
  const [firmLoading, setFirmLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);

  useEffect(() => {
    if (!id) {
      setFirmLoading(false);
      return;
    }

    import("../firebase").then(({ db }) => {
      import("firebase/firestore").then(({ doc, getDoc }) => {
        getDoc(doc(db, "firmalar", id)).then((snap) => {
          if (snap.exists()) {
            const d = snap.data();
            setFirm({
              id: snap.id,
              firma: d.firmaAdi || "",
              logo: d.logoUrl || "",
              logoUrl: d.logoUrl || "",
              sektor: d.secilenSektorler?.[0] || d.sektor || "",
              secilenSektorler: d.secilenSektorler || [],
              il: d.sehir || d.il || "",
              ilce: d.ilce || "",
              adres: d.acikAdres || d.adres || "",
              koy: d.sivasKoy || "",
              sivasIlce: d.sivasIlce || "",
              gsm: d.gsm || "",
              telefon: d.telefon || "",
              email: d.email || "",
              web: d.web || "",
              sosyalMedya: d.sosyalMedya || "",
              tanitimVideo: d.tanitimVideo || "",
              hakkinda: d.hakkinda || "",
              kurulusYili: d.kurulusYili || "",
              isletmeTuru: d.isletmeTuru || "",
              yetkiliKisi: d.yetkiliKisi || "",
              yetkiliPozisyon: d.yetkiliPozisyon || "",
              calismaSaatleri: d.calismaSaatleri || "",
              anahtarKelimeler: d.anahtarKelimeler || [],
              urunler: d.urunler || [],
              isGold: d.isGold || false,
              isVitrin: d.isVitrin || false,
              yorumlar: d.yorumlar || [],
            });
          }
          setFirmLoading(false);
        });
      });
    });
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (firmLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-slate-600 animate-spin" />
      </div>
    );
  }

  if (!firm) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
        <Building2 className="w-12 h-12 text-slate-300" />
        <p className="text-slate-500 text-sm">Firma bulunamadı.</p>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Geri dön
        </button>
      </div>
    );
  }

  const sectorLabel = normalizeSector(firm.sektor || "") || firm.sektor || "—";
  const locationFull =
    [firm.ilce, firm.il || firm.sehir]
      .filter(Boolean)
      .map(toTitleTR)
      .join(" / ") || "—";
  const hasMap =
    firm.adres || firm.acikAdres || firm.ilce || firm.il || firm.sehir;
  const keywords = firm.kelimeler
    ? firm.kelimeler
        .split(/[,،\s]+/)
        .map((k) => k.trim())
        .filter(Boolean)
    : Array.isArray(firm.anahtarKelimeler)
    ? firm.anahtarKelimeler
    : [];
  const products = Array.isArray(firm.urunler) ? firm.urunler : [];
  const phone = firm.gsm || firm.telefon || "";

  const handleAddComment = async () => {
    if (!rating) {
      alert("Lütfen puan seçin.");
      return;
    }

    if (!commentText.trim()) {
      alert("Lütfen yorum yazın.");
      return;
    }

    try {
      setCommentLoading(true);

      const [{ db, auth }, { doc, getDoc, updateDoc, arrayUnion, Timestamp }] =
        await Promise.all([
          import("../firebase"),
          import("firebase/firestore"),
        ]);

      const currentUser = auth.currentUser;

      if (!currentUser) {
        alert("Yorum yapabilmek için giriş yapmalısınız.");
        return;
      }

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);

      const userData = userSnap.exists() ? userSnap.data() : {};

      const newComment = {
        uid: currentUser.uid,
        name: userData.ad_soyad || currentUser.displayName || "Kullanıcı",
        yorum: commentText.trim(),
        puan: rating,
        tarih: Timestamp.now(),
        onay: false,
      };

      const firmRef = doc(db, "firmalar", id);

      await updateDoc(firmRef, {
        yorumlar: arrayUnion(newComment),
      });

      setFirm((prev) => ({
        ...prev,
        yorumlar: [...(prev.yorumlar || []), newComment],
      }));

      setCommentText("");
      setRating(0);
      setCommentSuccess(true);

      setTimeout(() => {
        setCommentSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Yorum eklenirken hata:", error);
      alert("Yorum eklenirken bir hata oluştu.");
    } finally {
      setCommentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="max-w-[1400px] mx-auto px-4 py-5">
        <nav className="flex items-center gap-2 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Firmalar
          </button>
          <span className="text-slate-300">/</span>
          <span className="text-sm text-slate-700 font-semibold truncate">
            {toTitleTR(firm.firma || firm.firmaAdi || "")}
          </span>
        </nav>

        {/* ÜST */}
        <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr_360px] gap-4 mb-4">
          {/* Logo */}
          <div className="bg-white border border-slate-200 shadow-sm p-4 min-h-[220px] flex items-center justify-center rounded-xl">
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <FirmaLogo
                  logo={firm.logo || firm.logoUrl}
                  name={firm.firma || firm.firmaAdi}
                  size="lg"
                />
              </div>

              <h1 className="text-lg font-extrabold text-slate-900 uppercase leading-tight">
                {toTitleTR(firm.firma || firm.firmaAdi || "Firma")}
              </h1>

              <p className="mt-1 text-xs text-slate-400 font-semibold">
                {sectorLabel}
              </p>

              <p className="mt-2 text-xs text-slate-500">{locationFull}</p>
            </div>
          </div>

          {/* İletişim */}
          <div className="bg-white border border-slate-200 shadow-sm p-5 min-h-[230px] rounded-xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              {[
                {
                  label: "Yetkili Kişi",
                  value: firm.yetkiliKisi || "Belirtilmemiş",
                },
                {
                  label: "İlçe / Köy",
                  value:
                    [firm.sivasIlce, firm.koy]
                      .filter(Boolean)
                      .map(toTitleTR)
                      .join(" / ") || "Belirtilmemiş",
                },
                {
                  label: "Adres",
                  value:
                    toTitleTR(firm.adres || firm.acikAdres) || "Belirtilmemiş",
                },
                {
                  label: "Telefon",
                  value: firm.telefon
                    ? formatPhone(firm.telefon)
                    : "Belirtilmemiş",
                  href: firm.telefon ? `tel:${firm.telefon}` : null,
                },
                {
                  label: "GSM",
                  value: firm.gsm ? formatPhone(firm.gsm) : "Belirtilmemiş",
                  href: firm.gsm ? `tel:${firm.gsm}` : null,
                },
                {
                  label: "Web Sitesi",
                  value: firm.web
                    ? firm.web.replace(/^https?:\/\/(www\.)?/, "")
                    : "Belirtilmemiş",
                  href: firm.web || null,
                },
                {
                  label: "E-Posta",
                  value: firm.email || "Belirtilmemiş",
                  href: firm.email ? `mailto:${firm.email}` : null,
                },
                {
                  label: "Sosyal Medya",
                  value: firm.sosyalMedya
                    ? firm.sosyalMedya.replace(/^https?:\/\/(www\.)?/, "")
                    : "Belirtilmemiş",
                  href: firm.sosyalMedya || null,
                },
                {
                  label: "Etiketler",
                  value: keywords.length
                    ? keywords.slice(0, 3).join(", ")
                    : "Belirtilmemiş",
                },
              ].map(({ label, value, href }, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[auto_12px_1fr] items-start border-b border-slate-100 py-3"
                >
                  <div className="text-sm font-bold text-slate-700">
                    {label?.toLocaleUpperCase("tr-TR")}
                  </div>
                  <div className="text-sm text-slate-400 font-bold">:</div>
                  <div className="text-sm text-slate-700 font-medium break-all min-w-0">
                    {href && value !== "Belirtilmemiş" ? (
                      <a
                        href={href}
                        target={href.startsWith("http") ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline transition"
                      >
                        {value}
                      </a>
                    ) : (
                      value
                    )}
                  </div>
                </div>
              ))}
            </div>

            {phone && (
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={`tel:${phone}`}
                  className="px-3 py-2 rounded-md bg-[#243f4d] text-white text-xs font-bold hover:bg-[#1d3441] transition"
                >
                  Ara: {formatPhone(phone)}
                </a>
              </div>
            )}
          </div>

          {/* Mail Form */}
          <div className="bg-[#243f4d] shadow-sm min-h-[230px] rounded-xl overflow-hidden">
            <div className="h-12 bg-[#1d3441] flex items-center px-4 gap-3">
              <Mail className="w-5 h-5 text-amber-400" />
              <h2 className="text-white font-extrabold text-sm uppercase">
                Firmaya E-Posta Gönder
              </h2>
            </div>

            <form className="p-4 space-y-3">
              <input
                type="text"
                placeholder="Adınız Soyadınız"
                className="w-full h-10 rounded-md px-3 text-xs font-semibold outline-none"
              />

              <input
                type="tel"
                placeholder="Telefon Numaranız"
                className="w-full h-10 rounded-md px-3 text-xs font-semibold outline-none"
              />

              <input
                type="email"
                placeholder="E-posta Adresiniz"
                className="w-full h-10 rounded-md px-3 text-xs font-semibold outline-none"
              />

              <textarea
                placeholder="Mesajınız"
                rows={3}
                className="w-full rounded-md px-3 py-2 text-xs font-semibold outline-none resize-none"
              />

              <button
                type="button"
                className="w-full h-10 bg-amber-400 hover:bg-amber-500 text-[#243f4d] text-xs font-extrabold uppercase rounded-md flex items-center justify-center gap-2 transition"
              >
                <Send className="w-4 h-4" />
                Mesajı Gönder
              </button>
            </form>
          </div>
        </div>

        {/* ALT */}
        <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-4">
          {/* Harita */}
          <div>
            {hasMap && (
              <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
                <SectionTitle icon={MapPin} title="Adres ve Harita Bilgisi" />

                <div className="p-4">
                  <FirmaMap
                    firma={{
                      adres: firm.adres || firm.acikAdres,
                      ilce: firm.ilce,
                      il: firm.il || firm.sehir,
                      firma: firm.firma || firm.firmaAdi,
                    }}
                  />
                </div>
              </div>
            )}

            <RatingBox rating={rating} setRating={setRating} />
          </div>

          {/* Hakkında + Ürünler */}
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
              <SectionTitle icon={Info} title="Firma Hakkında" />

              <div className="p-5">
                <p className="text-sm leading-7 text-slate-500 font-medium whitespace-pre-line">
                  {firm.hakkinda ||
                    "Firma hakkında açıklama henüz eklenmemiştir."}
                </p>
              </div>
            </div>

            {/* Tanıtım Videosu */}
            {firm.tanitimVideo && getYoutubeEmbedUrl(firm.tanitimVideo) && (
              <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
                <SectionTitle icon={PlayCircle} title="Tanıtım Filmi" />
                <div className="p-4">
                  <div
                    className="relative w-full"
                    style={{ paddingBottom: "56.25%" }}
                  >
                    <iframe
                      src={getYoutubeEmbedUrl(firm.tanitimVideo)}
                      title="Tanıtım Filmi"
                      className="absolute inset-0 w-full h-full rounded-lg"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ border: 0 }}
                    />
                  </div>
                </div>
              </div>
            )}

            {products.length > 0 && (
              <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
                <SectionTitle
                  icon={Package}
                  title="Ürünlerimiz / Hizmetlerimiz"
                />

                <div className="p-4">
                  <ProductsSection products={products} />
                </div>
              </div>
            )}
            <CommentSection
              rating={rating}
              setRating={setRating}
              commentText={commentText}
              setCommentText={setCommentText}
              commentLoading={commentLoading}
              onSubmit={handleAddComment}
              comments={
                Array.isArray(firm.yorumlar)
                  ? firm.yorumlar.filter((y) => y?.onay === true)
                  : []
              }
              commentSuccess={commentSuccess}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmDetailPage;
